import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    FormControl,
    InputLabel,
    OutlinedInput,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    ListSubheader,
    CircularProgress,
    Box,
    Button,
    TextField,
    Typography,
} from '@mui/material';
import { useDataProvider } from 'src/mtska-frontend-data-provider';
import { useTranslation } from 'react-i18next';
import { getFullscreenPortalContainer } from 'src/mtska-frontend-app-component/utils/fullscreenPortalContainer';

const defaultFilters = {};

const normalizeDistinct = (distinct) => {
    if (distinct == null || distinct === '') return [];
    return Array.isArray(distinct) ? distinct : [distinct];
};

const resolveSortFieldFromProject = (project) => {
    if (Array.isArray(project) && typeof project[0] === 'string' && project[0] !== '') {
        return project[0];
    }
    if (typeof project === 'string' && project !== '') {
        return project;
    }
    return null;
};

const dedupeOptionRows = (list, valueKey) => {
    const rows = list ?? [];
    const seen = new Set();
    return rows.filter((row) => {
        const v = row?.[valueKey];
        if (v == null || v === '') return false;
        if (seen.has(v)) return false;
        seen.add(v);
        return true;
    });
};

const stableKey = (value) => {
    if (value == null) return value;
    if (typeof value !== 'object') return value;
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
};

const keyEquals = (a, b) => stableKey(a) === stableKey(b);

const dedupeSelectionKeys = (values) => {
    if (!Array.isArray(values)) return [];
    const seen = new Set();
    const out = [];
    values.forEach((v) => {
        if (v == null || v === '') return;
        const k = stableKey(v);
        if (seen.has(k)) return;
        seen.add(k);
        out.push(v);
    });
    return out;
};

/** Da value (come InputAutocomplete): null = tutte; { [valueKey]: string | string[] } = cosa flaggare */
const selectionKeysFromValue = (value, valueKey) => {
    if (value == null) return null;
    const raw = value[valueKey];
    if (raw == null || raw === '') return null;
    if (Array.isArray(raw)) {
        const deduped = dedupeSelectionKeys(raw);
        return deduped.length > 0 ? deduped : null;
    }
    return [raw];
};

const InputFilterMultiCheckboxCore = ({
    valueKey = 'name',
    value = null,
    onChange,
    labelkey = 'name',
    label = 'Select',
    name,
    disabled,
    style,
    size = 'small',
    listMaxHeight = 360,
    searchPlaceholder,
    options,
    loading,
    multi = true,
}) => {
    const { t } = useTranslation();
    const labelId = name ?? `multi-checkbox-${valueKey}`;

    const allKeys = useMemo(() => options.map((o) => o[valueKey]), [options, valueKey]);

    const [selectedKeys, setSelectedKeys] = useState([]);
    const [menuSearch, setMenuSearch] = useState('');
    const [selectOpen, setSelectOpen] = useState(false);
    const searchInputRef = useRef(null);
    /** Con `multi`, `onChange` verso il parent solo alla chiusura del menu (evita richieste a ogni toggle). */
    const pendingMultiCommitRef = useRef({ selectionValue: null, dirty: false });

    const visibleOptions = useMemo(() => {
        const q = menuSearch.trim().toLowerCase();
        if (!q) return options;
        return options.filter((o) => {
            let text = '';
            if (Array.isArray(labelkey)) {
                text = `${o[labelkey[0]] ?? ''} ${o[labelkey[1]] ?? ''}`.trim();
            } else {
                text = String(o[labelkey] ?? '');
            }
            const keyStr = String(o[valueKey] ?? '');
            return `${text} ${keyStr}`.toLowerCase().includes(q);
        });
    }, [options, menuSearch, valueKey, labelkey]);

    const keysFromValue = selectionKeysFromValue(value, valueKey);
    const valueSyncKey =
        keysFromValue == null ? '__all__' : JSON.stringify([...keysFromValue].sort());

    const allKeysKey = allKeys.join('|');

    const getLabel = (option) => {
        if (!option) return '';
        if (Array.isArray(labelkey)) {
            return `${option[labelkey[0]] ?? ''} ${option[labelkey[1]] ?? ''}`.trim();
        }
        return option[labelkey] ?? '';
    };

    useEffect(() => {
        if (!allKeys.length) {
            setSelectedKeys([]);
            return;
        }
        if (keysFromValue == null) {
            setSelectedKeys(multi ? allKeys : []);
            return;
        }
        const next = keysFromValue.filter((v) => allKeys.some((k) => keyEquals(k, v)));
        if (next.length) {
            setSelectedKeys(multi ? next : [next[0]]);
            return;
        }
        setSelectedKeys(multi ? allKeys : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- valueSyncKey, allKeysKey
    }, [valueSyncKey, allKeysKey, multi]);

    const publishSelection = (selectionValue, uiKeys) => {
        setSelectedKeys(uiKeys);
        if (multi) {
            pendingMultiCommitRef.current = { selectionValue, dirty: true };
            return;
        }
        onChange(selectionValue, { allKeys, valueKey });
    };

    const onSelectChange = (event) => {
        const raw = event.target.value;
        if (!multi) {
            if (raw == null || raw === '') {
                publishSelection(null, []);
                return;
            }
            publishSelection({ [valueKey]: raw }, [raw]);
            handleSelectClose();
            return;
        }
        const nextRaw = typeof raw === 'string' ? raw.split(',') : raw;
        const next = dedupeSelectionKeys(nextRaw);

        if (!next.length) {
            publishSelection(null, [...allKeys]);
            return;
        }
        if (next.length === allKeys.length) {
            publishSelection(null, [...allKeys]);
            return;
        }
        publishSelection({ [valueKey]: next }, next);
    };

    const handleSelectClose = () => {
        if (multi && pendingMultiCommitRef.current.dirty) {
            const { selectionValue } = pendingMultiCommitRef.current;
            pendingMultiCommitRef.current = { selectionValue: null, dirty: false };
            onChange(selectionValue, { allKeys, valueKey });
        }
        setSelectOpen(false);
        setMenuSearch('');
    };

    const selectOnly = (key) => {
        publishSelection(
            { [valueKey]: multi ? [key] : key },
            [key],
        );
        handleSelectClose();
    };

    const selectedLabelsText = (selected) =>
        selected
            .map((key) => {
                const o = options.find((x) => keyEquals(x[valueKey], key));
                return getLabel(o) || key;
            })
            .join(', ');

    const allOptionsSelected = multi
        ? allKeys.length > 0 && allKeys.every((k) => selectedKeys.some((s) => keyEquals(s, k)))
        : selectedKeys.length === 0;
    const someButNotAllSelected = multi
        ? !allOptionsSelected &&
          allKeys.length > 0 &&
          selectedKeys.some((s) => allKeys.some((k) => keyEquals(s, k)))
        : false;

    const renderSelected = (selected) => {
        if (!allKeys.length) return '';
        const selectedArray =
            selected == null || selected === '' ? [] : (Array.isArray(selected) ? selected : [selected]);
        const body = selectedArray.length === 0
            ? t('all')
            : (multi && selectedArray.length === allKeys.length ? t('all') : selectedLabelsText(selectedArray));
        if (body === '') return '';
        return (
            <Box
                component="span"
                sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {body}
            </Box>
        );
    };

    return (
        <FormControl sx={{ minWidth: 0, ...(style || {}) }} size={size} disabled={disabled || loading} fullWidth>
            <InputLabel id={labelId} shrink>
                {label}
            </InputLabel>
            <Box sx={{ position: 'relative', width: '100%', minWidth: 0 }}>
                <Select
                    name={name}
                    labelId={labelId}
                    multiple={multi}
                    displayEmpty={!multi}
                    fullWidth
                    open={selectOpen}
                    onOpen={() => {
                        setSelectOpen(true);
                        if (multi) {
                            pendingMultiCommitRef.current = { selectionValue: null, dirty: false };
                        }
                        requestAnimationFrame(() => {
                            searchInputRef.current?.focus();
                        });
                    }}
                    onClose={handleSelectClose}
                    value={multi ? selectedKeys : (selectedKeys[0] ?? '')}
                    onChange={onSelectChange}
                    input={<OutlinedInput label={label} />}
                    renderValue={renderSelected}
                    sx={{
                        maxWidth: '100%',
                        minWidth: 0,
                        '& .MuiSelect-select': {
                            display: 'flex',
                            alignItems: 'center',
                            minWidth: 0,
                            overflow: 'hidden',
                        },
                    }}
                    MenuProps={{
                        PaperProps: { style: { maxHeight: listMaxHeight } },
                        autoFocus: false,
                        container: getFullscreenPortalContainer,
                        MenuListProps: {
                            sx: { paddingTop: 0 },
                        },
                    }}
                >
                {!loading && (
                    <ListSubheader
                        sx={{
                            py: 0.75,
                            px: 0.5,
                            lineHeight: 1.25,
                            backgroundColor: 'var(--app-maincanvas-border-color)',
                            position: 'sticky',
                            top: 0,
                            zIndex: 2,
                            borderBottom: 1,
                            borderColor: 'divider',
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                mb: 0.75,
                            }}
                        >
                            <Checkbox
                                size="small"
                                checked={allOptionsSelected}
                                indeterminate={someButNotAllSelected}
                                disabled={allKeys.length === 0 || !multi}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (allKeys.length === 0) return;
                                    publishSelection(null, multi ? [...allKeys] : []);
                                }}
                            />
                            <Typography
                                variant="body2"
                                component="span"
                                sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}
                            >
                                {label}
                            </Typography>
                        </Box>
                        <TextField
                            inputRef={searchInputRef}
                            size="small"
                            fullWidth
                            placeholder={searchPlaceholder ?? t('Type to search')}
                            value={menuSearch}
                            onChange={(e) => setMenuSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                                if (e.key === 'Escape') return;
                                e.stopPropagation();
                            }}
                            sx={{
                                '& .MuiOutlinedInput-input': {
                                    backgroundColor: 'white',
                                },
                            }}
                        />
                    </ListSubheader>
                )}
                {loading && (
                    <MenuItem disabled>
                        <Box display="flex" alignItems="center" gap={1}>
                            <CircularProgress size={18} />
                        </Box>
                    </MenuItem>
                )}
                {!loading && visibleOptions.length === 0 && (
                    <MenuItem disabled>{t('No options found')}</MenuItem>
                )}
                {!loading && !multi && (
                    <MenuItem value="" dense>
                        <Checkbox size="small" checked={selectedKeys.length === 0} />
                        <ListItemText primary={t('all')} sx={{ flex: '1 1 auto', minWidth: 0, mr: 0.5 }} />
                    </MenuItem>
                )}
                {!loading &&
                    visibleOptions.map((o) => {
                        const v = o[valueKey];
                        const checked = selectedKeys.some((k) => keyEquals(k, v));
                        return (
                            <MenuItem key={stableKey(v)} value={v} dense>
                                <Checkbox size="small" checked={checked} />
                                <ListItemText primary={getLabel(o)} sx={{ flex: '1 1 auto', minWidth: 0, mr: 0.5 }} />
                                {multi && (
                                    <Button
                                        type="button"
                                        size="small"
                                        variant="text"
                                        sx={{
                                            flexShrink: 0,
                                            minWidth: 0,
                                            px: 0.75,
                                            py: 0.25,
                                            fontSize: '0.7rem',
                                            lineHeight: 1.2,
                                            textTransform: 'none',
                                        }}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            selectOnly(v);
                                        }}
                                    >
                                        {t('only')}
                                    </Button>
                                )}
                            </MenuItem>
                        );
                    })}
                </Select>
                {loading && (
                    <CircularProgress
                        size={14}
                        thickness={4.5}
                        sx={{
                            position: 'absolute',
                            right: (theme) => theme.spacing(4),
                            top: '50%',
                            marginTop: '-7px',
                            zIndex: 2,
                            pointerEvents: 'none',
                            color: 'grey.600',
                        }}
                    />
                )}
            </Box>
        </FormControl>
    );
};

const InputFilterMultiCheckboxFromServer = ({
    dataProvider,
    dataProviderName,
    valueKey = 'name',
    value = null,
    onChange,
    labelkey = 'name',
    filters = defaultFilters,
    project = [],
    distinct = [],
    label = 'Select',
    name,
    disabled,
    style,
    size = 'small',
    listMaxHeight = 360,
    perPage = 2000,
    searchPlaceholder,
    sourceField,
    multi = true,
}) => {
    const dp = dataProvider ?? dataProviderName;
    const { items: optionRows, loading, loadAll } = useDataProvider(dp);

    const options = useMemo(
        () => dedupeOptionRows(optionRows, valueKey),
        [optionRows, valueKey],
    );

    const sortField = resolveSortFieldFromProject(project);
    const sortModel = useMemo(
        () => (sortField ? [{ field: sortField, sort: 'asc' }] : []),
        [sortField],
    );

    const hydrateRequestKey = JSON.stringify({
        items: filters?.items ?? [],
        project,
        distinct: normalizeDistinct(distinct),
        sort: sortModel,
        sourceField: sourceField ?? null,
    });

    useEffect(() => {
        const compoundfilters = {
            items: filters?.items ?? [],
            project,
            distinct,
            ...(sourceField ? { sourceField } : {}),
        };
        loadAll(compoundfilters, sortModel, 1, perPage, null, {
            cacheKey: `hydration:${hydrateRequestKey}`,
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dp, perPage, hydrateRequestKey
    }, [dp, perPage, hydrateRequestKey]);

    return (
        <InputFilterMultiCheckboxCore
            valueKey={valueKey}
            value={value}
            onChange={onChange}
            labelkey={labelkey}
            label={label}
            name={name}
            disabled={disabled}
            style={style}
            size={size}
            listMaxHeight={listMaxHeight}
            searchPlaceholder={searchPlaceholder}
            options={options}
            loading={loading}
            multi={multi}
        />
    );
};

const InputFilterMultiCheckboxStatic = ({
    staticOptions,
    valueKey = 'name',
    value = null,
    onChange,
    labelkey = 'name',
    label = 'Select',
    name,
    disabled,
    style,
    size = 'small',
    listMaxHeight = 360,
    searchPlaceholder,
    multi = true,
}) => {
    const options = useMemo(
        () => dedupeOptionRows(staticOptions, valueKey),
        [staticOptions, valueKey],
    );

    return (
        <InputFilterMultiCheckboxCore
            valueKey={valueKey}
            value={value}
            onChange={onChange}
            labelkey={labelkey}
            label={label}
            name={name}
            disabled={disabled}
            style={style}
            size={size}
            listMaxHeight={listMaxHeight}
            searchPlaceholder={searchPlaceholder}
            options={options}
            loading={false}
            multi={multi}
        />
    );
};

/**
 * Filtro a checkbox configurabile multi/single. `value` null = tutte (nessun filtro).
 * `multi=true` emette `{ [valueKey]: string[] }` alla chiusura del menu (non a ogni toggle); `multi=false` emette `{ [valueKey]: string }` subito.
 * Con `staticOptions` (array) le opzioni non vengono caricate dal server.
 */
const InputFilterMultiCheckbox = (props) => {
    const { staticOptions, ...serverProps } = props;
    if (staticOptions != null) {
        return <InputFilterMultiCheckboxStatic {...props} />;
    }
    return <InputFilterMultiCheckboxFromServer {...serverProps} />;
};

export default InputFilterMultiCheckbox;
